import {
  Button, CodeDialog, CopyButton, Dialog, MoreMenu, Notice, PageFrame, PersonStateMark, ProblemBox, Table, type TableColumn,
} from "@don/design-system";
import type * as React from "react";
import { useEffect, useRef, useState } from "react";
import type { IssuedPairingCode, PeopleRoster, TeamMember } from "@don/wire";

import { GettingStartedLink, GuideDownloadButton } from "../shell/recorder-links.js";

import { withAdded } from "./add.js";
import { AddTeammateDialog } from "./AddTeammateDialog.js";
import { deactivateCopy, deactivateFailedCopy, withDeactivated } from "./deactivate.js";
import { withEdited } from "./edit.js";
import { EditTeammateDialog } from "./EditTeammateDialog.js";
import {
  reactivateCopy, reactivateFailedCopy, targetOf, withReactivated, type ReactivateTarget,
} from "./reactivate.js";

import {
  codePanelCopy, newCodeFailedCopy, orderRoster, pendingCode, problemTip, recordingsLabel, roleLabel,
  ROW_ACTION_LABEL, ROW_ACTION_RULE_BEFORE, rowActions, teamStatus, withIssuedCode, type RowAction, type TeamSource,
} from "./team.js";

/**
 * Settings · Team (epic 26-10-02-knowledge-v0, issue 212): avatar → Settings opens straight to
 * this table on the `single` page frame, with no side nav (decision 1).
 *
 * **Three states, never confused** (issue 90's "null is not empty"): `loading` is a static
 * skeleton, `failed` is the problem box with Try again, and only an answer draws rows. A
 * failed Try again leaves the box as it was. The rows are the server's, ordered by
 * `orderRoster`; nothing here patches them.
 *
 * **Row actions** (issue 214): the ⋯ offers New pairing code on every row but a deactivated
 * one (`rowActions`). A mint opens the code panel and folds the new code into the rows
 * (`withIssuedCode`), so the row behind the scrim already reads as it will after Done; a
 * failed mint shows the page Notice and changes nothing.
 *
 * **Deactivate** (issue 217) asks first (DS `Dialog`: Deactivate outline, Cancel primary
 * and focused; Escape and the scrim are Cancel). While the server has not answered the
 * confirm stays, reading Deactivating…, with both buttons, Escape and the scrim inert, so it
 * cannot be sent twice (217-ux P1-1). Once the server has answered, the row is folded to
 * Deactivated (`withDeactivated`), where `orderRoster` puts it in the deactivated group, and
 * focus goes to that row's ⋯ (design-spec §8 row 17, 208 debt B1). A failed deactivate closes
 * the confirm, shows the page Notice and changes nothing.
 *
 * **Reactivate** (issue 218) is a deactivated row's only ⋯ item, and Add's offer band leads to
 * it too. It asks first (DS `Dialog` variant `confirm`: Cancel, then Reactivate primary and
 * focused; Escape and the scrim are Cancel), busy as Deactivate is (Reactivating…). Once the
 * server answers the fresh code, the row is folded to Pending invite with it
 * (`withReactivated`), the code panel opens, and Done puts focus on that row's ⋯ whichever way
 * Reactivate was reached (design-spec §12). A failed reactivate closes the confirm, shows the
 * page Notice and changes nothing.
 *
 * **Edit** (issue 216) opens `EditTeammateDialog` on a teammate's row. Once the server answers
 * `edited`, its row replaces the old one in place (`withEdited`), in name order.
 *
 * **Add teammate** (issue 215) opens `AddTeammateDialog`. Once the server answers `added`, its
 * row joins the table in name order (`withAdded`), tinted while the code panel over it is open,
 * and Done returns focus to Add teammate.
 */
type Read =
  | { readonly kind: "loading" }
  | { readonly kind: "failed"; readonly retrying: boolean }
  | { readonly kind: "loaded"; readonly roster: PeopleRoster };

const UNAVAILABLE = "Available once your team loads";
const NUDGE = "Add your first teammate to give them a code for the recorder.";

/**
 * The code panel open over the table: whose code, the code itself, and whether Add or Reactivate
 * made it.
 */
interface Panel {
  readonly member: Pick<TeamMember, "person_id" | "display_name" | "role">;
  readonly issued: IssuedPairingCode;
  readonly added?: boolean;
  readonly reactivated?: boolean;
}

/** design-spec §4 Table grid and §4 Skeleton widths. */
const COLUMNS: readonly TableColumn[] = [
  {
    key: "name",
    label: "Name",
    width: "minmax(0, 1fr)",
    skeleton: [[160, 210], [140, 190], [170, 230], [120, 180], [150, 200], [130, 210]],
  },
  { key: "role", label: "Role", width: "140px", skeleton: [[64]] },
  { key: "status", label: "Status", width: "210px", skeleton: [[76]] },
  { key: "recordings", label: "Recordings", width: "190px", skeleton: [[96, 52]] },
  { key: "actions", label: "", ariaLabel: "Actions", width: "44px", end: true },
];

export function TeamScreen({ source, nowMs }: { source: TeamSource; nowMs: number }) {
  const [read, setRead] = useState<Read>({ kind: "loading" });
  const [panel, setPanel] = useState<Panel | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [confirming, setConfirming] = useState<TeamMember | null>(null);
  const [reactivating, setReactivating] = useState<ReactivateTarget | null>(null);
  // A confirm whose request is out: its buttons, Escape and the scrim do nothing until it answers.
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [focusRow, setFocusRow] = useState<string | null>(null);
  // One mint at a time: a second ⋯ choice while one is in flight is ignored.
  const minting = useRef(false);
  // One confirmed act at a time, so a second confirm while one is out sends nothing.
  const acting = useRef(false);

  async function newCode(member: TeamMember) {
    if (minting.current) return;
    minting.current = true;
    setNotice(null);
    try {
      const issued = await source.issueCode(member.person_id);
      setRead((now) => (now.kind === "loaded" ? { kind: "loaded", roster: withIssuedCode(now.roster, issued) } : now));
      setPanel({ member, issued });
    } catch {
      // The rows stay as they were; the Notice is the only change on the page.
      setNotice(newCodeFailedCopy(member));
    } finally {
      minting.current = false;
    }
  }

  async function deactivate(member: TeamMember) {
    if (acting.current) return;
    acting.current = true;
    setConfirmBusy(true);
    setNotice(null);
    try {
      await source.deactivate(member.person_id);
    } catch {
      // The rows stay as they were; the Notice is the only change on the page.
      setConfirming(null);
      setNotice(deactivateFailedCopy(member));
      return;
    } finally {
      acting.current = false;
      setConfirmBusy(false);
    }
    setConfirming(null);
    setRead((now) =>
      now.kind === "loaded" ? { kind: "loaded", roster: withDeactivated(now.roster, member.person_id, nowMs) } : now,
    );
    setFocusRow(member.person_id);
  }

  async function reactivate(target: ReactivateTarget) {
    if (acting.current) return;
    acting.current = true;
    setConfirmBusy(true);
    setNotice(null);
    let issued: IssuedPairingCode;
    try {
      issued = await source.reactivate(target.person_id);
    } catch {
      // The rows stay as they were; the Notice is the only change on the page.
      setReactivating(null);
      setNotice(reactivateFailedCopy(target));
      return;
    } finally {
      acting.current = false;
      setConfirmBusy(false);
    }
    setRead((now) => (now.kind === "loaded" ? { kind: "loaded", roster: withReactivated(now.roster, issued) } : now));
    setReactivating(null);
    // Only an employee is ever deactivated (the owner's row offers no Deactivate, and the
    // server refuses it), so the panel names them by first name.
    setPanel({
      member: { person_id: target.person_id, display_name: target.display_name, role: "employee" },
      issued,
      reactivated: true,
    });
  }

  // After a deactivate, once the moved row has rendered: its ⋯.
  useEffect(() => {
    if (focusRow === null) return;
    document.querySelector<HTMLElement>(`[data-team-more="${CSS.escape(focusRow)}"]`)?.focus();
    setFocusRow(null);
  }, [focusRow]);

  useEffect(() => {
    let live = true;
    setRead({ kind: "loading" });
    source.read().then(
      (roster) => live && setRead({ kind: "loaded", roster }),
      () => live && setRead({ kind: "failed", retrying: false }),
    );
    return () => {
      live = false;
    };
  }, [source]);

  async function retry() {
    if (read.kind !== "failed" || read.retrying) return;
    setRead({ kind: "failed", retrying: true });
    try {
      setRead({ kind: "loaded", roster: await source.read() });
    } catch {
      // Still failed: the box stays as it was, and Try again is offered again.
      setRead({ kind: "failed", retrying: false });
    }
  }

  function added(person: TeamMember, issued: IssuedPairingCode) {
    setRead((now) => (now.kind === "loaded" ? { kind: "loaded", roster: withAdded(now.roster, person) } : now));
    setAdding(false);
    setPanel({ member: person, issued, added: true });
  }

  function edited(person: TeamMember) {
    setRead((now) => (now.kind === "loaded" ? { kind: "loaded", roster: withEdited(now.roster, person) } : now));
    setEditing(null);
  }

  function done() {
    const afterAdd = panel?.added === true;
    const reactivated = panel?.reactivated === true ? panel.member.person_id : null;
    setPanel(null);
    // After Add the panel's opener was the closed form, so focus goes back to Add teammate
    // explicitly, once the dialog's own restore has run (design-spec §9 "Flow: Add").
    if (afterAdd) requestAnimationFrame(() => document.querySelector<HTMLElement>("[data-team-add]")?.focus());
    // After Reactivate, the row's ⋯, whether the confirm was reached from that ⋯ or from Add's
    // offer (design-spec §12): the restore would otherwise land on Add teammate.
    if (reactivated !== null) {
      requestAnimationFrame(() =>
        document.querySelector<HTMLElement>(`[data-team-more="${CSS.escape(reactivated)}"]`)?.focus(),
      );
    }
  }

  const loaded = read.kind === "loaded";
  // Only the owner on the team: the nudge says what Add teammate is for (design-spec §5).
  const onlyOwner = loaded && read.roster.people.every((m) => m.role === "owner");
  return (
    <div data-settings="" data-team="" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <PageFrame variant="single">
        <div
          style={{
            display: "flex", alignItems: "flex-start", gap: "var(--space-25)",
            marginBottom: "var(--space-3)",
          }}
        >
          <h1
            style={{
              margin: "0 auto 0 0", fontSize: "var(--type-display-size)",
              lineHeight: "var(--type-display-line)", fontWeight: "var(--type-display-weight)",
              letterSpacing: "var(--type-display-tracking)",
            }}
          >
            Team
          </h1>
          {onlyOwner && (
            <span
              data-team-nudge=""
              style={{
                color: "var(--ink-2)", fontSize: "var(--type-small-size)", lineHeight: "36px",
              }}
            >
              {NUDGE}
            </span>
          )}
          <GuideDownloadButton />
          <Button
            variant="primary"
            data-team-add=""
            disabled={!loaded}
            // The reason a disabled control is disabled (P6); `title` is also its accessible
            // description.
            title={loaded ? undefined : UNAVAILABLE}
            onClick={() => loaded && setAdding(true)}
          >
            Add teammate
          </Button>
        </div>
        {notice !== null && (
          <div data-team-notice="" style={{ marginBottom: "var(--space-3)" }}>
            <Notice tone="failed" onClose={() => setNotice(null)}>
              {notice}
            </Notice>
          </div>
        )}
        {read.kind === "failed" ? (
          <ProblemBox wide onRetry={() => void retry()} retryBusy={read.retrying}>
            <b style={{ fontWeight: "var(--type-body-weight-current)" }}>Couldn't load your team.</b>{" "}
            The server didn't answer; nothing was changed.
          </ProblemBox>
        ) : (
          <Table
            data-team-table=""
            aria-label="Team"
            columns={COLUMNS}
            loading={read.kind === "loading"}
            rows={
              read.kind === "loaded"
                ? orderRoster(read.roster.people).map((m) =>
                    row(
                      m,
                      nowMs,
                      {
                        "new-code": () => void newCode(m),
                        edit: () => setEditing(m),
                        deactivate: () => setConfirming(m),
                        reactivate: () => setReactivating(targetOf(m)),
                      },
                      panel?.added === true && panel.member.person_id === m.person_id,
                    ),
                  )
                : []
            }
          />
        )}
        {adding && (
          <AddTeammateDialog
            add={(request) => source.add(request)}
            onAdded={added}
            onReactivate={(target) => {
              setAdding(false);
              setReactivating(target);
            }}
            onClose={() => setAdding(false)}
          />
        )}
        {editing !== null && (
          <EditTeammateDialog
            member={editing}
            edit={(personId, request) => source.edit(personId, request)}
            onEdited={edited}
            onClose={() => setEditing(null)}
          />
        )}
        {confirming !== null && (
          <ConfirmDeactivate
            member={confirming}
            busy={confirmBusy}
            onKeep={() => !confirmBusy && setConfirming(null)}
            onConfirm={() => void deactivate(confirming)}
          />
        )}
        {reactivating !== null && (
          <ConfirmReactivate
            target={reactivating}
            busy={confirmBusy}
            onCancel={() => !confirmBusy && setReactivating(null)}
            onConfirm={() => void reactivate(reactivating)}
          />
        )}
        {panel !== null && <CodePanel member={panel.member} issued={panel.issued} onDone={done} />}
      </PageFrame>
    </div>
  );
}

/**
 * The code panel (issue 214): the code, who types it and when it expires, and `Getting started`
 * under that sentence (issue 260), since the panel is what the owner sends a teammate.
 */
export function CodePanel({ member, issued, onDone }: Omit<Panel, "added" | "reactivated"> & { onDone: () => void }) {
  const copy = codePanelCopy(member, issued);
  return (
    <CodeDialog
      title={copy.title}
      code={issued.code}
      onDone={onDone}
      after={<GettingStartedLink />}
      data-team-code-panel={member.person_id}
    >
      {copy.sentence}
    </CodeDialog>
  );
}

/** Deactivate's confirm (design-spec §5 `deactivate-confirm`, §6, §11 C8), busy while out. */
function ConfirmDeactivate({
  member, busy, onKeep, onConfirm,
}: {
  member: TeamMember;
  busy: boolean;
  onKeep: () => void;
  onConfirm: () => void;
}) {
  const copy = deactivateCopy(member);
  return (
    <Dialog
      title={copy.title}
      onClose={onKeep}
      destructive={{
        children: busy ? copy.busy : copy.confirm,
        disabled: busy,
        onClick: onConfirm,
        "data-team-deactivate-confirm": member.person_id,
      }}
      safe={{ children: copy.cancel, disabled: busy, onClick: onKeep, "data-team-deactivate-keep": member.person_id }}
    >
      {copy.body}
    </Dialog>
  );
}

/** Reactivate's confirm (design-spec §2 `Dialog` variant `confirm`, §5 `reactivate-confirm`). */
function ConfirmReactivate({
  target, busy, onCancel, onConfirm,
}: {
  target: ReactivateTarget;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const copy = reactivateCopy(target);
  return (
    <Dialog
      variant="confirm"
      title={copy.title}
      onClose={onCancel}
      confirm={{
        children: busy ? copy.busy : copy.confirm,
        disabled: busy,
        onClick: onConfirm,
        "data-team-reactivate-confirm": target.person_id,
      }}
      cancel={{ children: copy.cancel, disabled: busy, onClick: onCancel, "data-team-reactivate-cancel": target.person_id }}
    >
      {copy.body}
    </Dialog>
  );
}

/**
 * The Status cell: the mark and word, and on a problem word the tooltip that says what is
 * wrong and what to do (issue 213). Active, Pending invite, Deactivated and Not set up have
 * no tooltip and nothing to focus (`team.test.ts`).
 */
export function StatusCell({ member }: { member: TeamMember }) {
  const status = teamStatus(member);
  const tip = problemTip(member);
  const code = pendingCode(member);
  return (
    <PersonStateMark
      state={status.mark}
      tip={tip ? { title: tip.problem, detail: tip.fix } : undefined}
      detail={code === null ? undefined : <PendingCode member={member} code={code} />}
    >
      {status.word}
    </PersonStateMark>
  );
}

/**
 * A Pending invite's live code under the word, with its copy icon (design-spec §4 "Pending code
 * line": height 24, 2 below the word, the code 14/500 letter-spaced and tabular, ink-2).
 */
function PendingCode({ member, code }: { member: TeamMember; code: string }) {
  return (
    <span
      data-team-code=""
      style={{ display: "flex", alignItems: "center", gap: "var(--space-025)", height: 24, marginTop: "var(--space-025)" }}
    >
      <span
        data-team-code-text=""
        style={{
          fontWeight: "var(--type-small-weight-strong)", letterSpacing: "var(--type-code-small-tracking)",
          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        }}
      >
        {code}
      </span>
      <CopyButton text={code} size="icon" label={`Copy code for ${member.display_name}`} />
    </span>
  );
}

const ellipsis: React.CSSProperties = { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/** Each ⋯ item's handler, keyed as `ROW_ACTION_LABEL` is, so every item has both. */
type RowHandlers = Readonly<Record<RowAction, () => void>>;

function row(member: TeamMember, nowMs: number, handlers: RowHandlers, tinted: boolean) {
  const actions = rowActions(member);
  const status = teamStatus(member);
  const gone = member.status === "deactivated";
  const recordings = recordingsLabel(member, nowMs);
  const quiet = gone ? "var(--ink-2)" : "var(--ink-1)";
  return {
    key: member.person_id,
    tinted,
    data: { "data-team-row": member.person_id, "data-team-status": status.kind },
    cells: {
      name: (
        <>
          <span
            data-team-name=""
            title={member.display_name}
            style={{
              ...ellipsis, color: quiet,
              fontWeight: gone ? "var(--type-body-weight-strong)" : "var(--type-body-weight-current)",
            }}
          >
            {member.display_name}
          </span>
          {member.email !== null && (
            <span
              data-team-email=""
              title={member.email}
              style={{
                ...ellipsis, color: "var(--ink-2)", fontSize: "var(--type-small-size)", lineHeight: 1.4,
              }}
            >
              {member.email}
            </span>
          )}
        </>
      ),
      role: <span style={{ color: quiet }}>{roleLabel(member)}</span>,
      status: <StatusCell member={member} />,
      recordings:
        recordings === null ? (
          <span data-team-recordings="none" aria-label="None" style={{ color: "var(--ink-2)" }}>
            –
          </span>
        ) : (
          <span data-team-recordings="" style={{ display: "block", fontVariantNumeric: "tabular-nums", color: quiet }}>
            <span style={{ display: "block" }}>{recordings.last}</span>
            <span
              style={{
                display: "block", color: "var(--ink-2)", fontSize: "var(--type-small-size)", lineHeight: 1.4,
              }}
            >
              {recordings.total}
            </span>
          </span>
        ),
      actions:
        actions.length === 0 ? null : (
          <MoreMenu
            label={`Actions for ${member.display_name}`}
            data-team-more={member.person_id}
            items={actions.map((action) => ({
              id: action,
              label: ROW_ACTION_LABEL[action],
              onSelect: handlers[action],
              ruleBefore: ROW_ACTION_RULE_BEFORE.has(action),
            }))}
          />
        ),
    },
  };
}
